import { FC, useMemo } from 'react';
import {
    Button,
    Checkbox,
    Dropdown,
    Space,
    Tooltip,
} from 'antd';
import type { MenuProps } from 'antd';
import { IoReloadOutline } from 'react-icons/io5';
import { FiDownload, FiSettings } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface TableActionsProps {
    columnsSelected?: string[];
    availableColumns?: string[];
    columnLabels?: Record<string, string>;
    onColumnsChange?: (columns: string[]) => void;
    onExport?: () => void;
    onReload?: () => void;
    exportDisabled?: boolean;
}

const TableActions: FC<TableActionsProps> = ({
    columnsSelected,
    availableColumns,
    columnLabels,
    onColumnsChange,
    onExport,
    onReload,
    exportDisabled,
}) => {
    const { t } = useTranslation('shared', { keyPrefix: 'TABLE' });

    const columnMenuItems: MenuProps['items'] = useMemo(() => (
        availableColumns?.map((col) => ({
            key: col,
            label: (
                <Checkbox
                    checked={columnsSelected?.includes(col)}
                    onChange={(e) => {
                        if (!onColumnsChange || !columnsSelected) return;
                        const next = e.target.checked
                            ? [...columnsSelected, col]
                            : columnsSelected.filter((c) => c !== col);
                        onColumnsChange(next);
                    }}
                >
                    {columnLabels?.[col] ?? col}
                </Checkbox>
            ),
        }))
    ), [availableColumns, columnsSelected, columnLabels, onColumnsChange]);

    return (
        <Space size={8}>
            {onColumnsChange && columnMenuItems && (
                <Dropdown menu={{ items: columnMenuItems }} trigger={['click']} placement="bottomRight">
                    <Tooltip title={t('CUSTOM_COLUMNS')}>
                        <Button icon={<FiSettings size={16} style={{ verticalAlign: 'middle' }} />} />
                    </Tooltip>
                </Dropdown>
            )}
            {onExport && (
                <Tooltip title={t('DOWNLOAD_TABLE')}>
                    <Button
                        icon={<FiDownload size={16} style={{ verticalAlign: 'middle' }} />}
                        onClick={onExport}
                        disabled={exportDisabled}
                    />
                </Tooltip>
            )}
            {onReload && (
                <Tooltip title={t('REFRESH')}>
                    <Button
                        icon={<IoReloadOutline size={16} style={{ verticalAlign: 'middle' }} />}
                        onClick={onReload}
                    />
                </Tooltip>
            )}
        </Space>
    );
};

export { TableActions };
